import { Document, Page, pdf, Text, View } from "@react-pdf/renderer"
import { styles, TableCell } from "components"
import moment from "moment"
import { getSourceAndLink } from "pages/FindSection/functions"
import { cellValuePdf, RowExportTypePdf } from "types"
import { convertNumber, useHandleAuth } from "utils"
import { DataList } from "../types"

type UseExportPdf = {
  data: DataList[]
  dateFilter: string,
  module?: string
}

export const useExportPdfFindInventory = ({
  data,
  dateFilter,
  module
}: UseExportPdf) => {
  const auth = useHandleAuth()
  const token = auth.userTokenData()

  const columns = [
    { header: "NO", key: "no", width: 5 },
    { header: "DATE", key: "date", width: 15 },
    { header: "BILL ID#", key: "bill_id", width: 25 },
    { header: "SOURCE", key: "source", width: 15 },
    { header: "ACCOUNT", key: "account", width: 15 },
    { header: "ACCOUNT NAME", key: "account_name", width: 15 },
    { header: "MEMO", key: "memo", width: 15 },
    { header: "QTY", key: "qty", width: 15 },
    { header: "UNIT", key: "unit", width: 15 },
    { header: "DEBIT", key: "debit", width: 15 },
    { header: "CREDIT", key: "credit", width: 15 },
    { header: "JOB ID", key: "job_id", width: 15 },
    { header: "LOCATION", key: "location", width: 15 }
  ]

  type ColumnKey = (typeof columns)[number]["key"]

  type ColumnObject = {
    [K in ColumnKey]: any
  }

  function numberFormat(value: number): cellValuePdf {
    return {
      text: convertNumber(value).intoCurrency,
      style: {
        textAlign: "right",
        fontWeight: "bold"
      }
    }
  }

  const calculate = data.reduce(
    (acc, item) => {
      return {
        debit: acc.debit + (item.position === 1 ? item.amount : 0),
        credit: acc.credit + (item.position === 2 ? item.amount : 0)
      }
    },
    { debit: 0, credit: 0 }
  )

  const dataMap: RowExportTypePdf[] = data.map((item, index) => {
    return {
      no: (index + 1).toString(),
      date: moment(item.transaction_date).format("DD/MM/YYYY"),
      bill_id: item.trx_code,
      source: item.data_source
        ? getSourceAndLink(item.data_source, item)?.source
        : "-",
      account: `${item?.account.group_coa_code} - ${item?.account.coa_code}`,
      account_name: item.account.coa_name,
      memo: item.memo,
      qty: numberFormat(item.qty ?? 0),
      unit: item.unit?.mr_unit_name ?? "",
      debit:
        item.position === 1 && item.amount > 0 ? numberFormat(item.amount) : "",
      credit:
        item.position === 2 && item.amount > 0 ? numberFormat(item.amount) : "",
      job_id: item.project.project_name,
      location: item.location.location_name
    } as ColumnObject
  })

  return {
    handleExportPdf: () =>
      pdf(
        <PdfTemplate
          data={dataMap}
          column={columns}
          date={dateFilter}
          credit={calculate.credit}
          debit={calculate.debit}
          companyName={token?.company_name}
          module={module}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = `find-records-inventory-${dateFilter}.pdf`
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

function PdfTemplate({
  data,
  column,
  date,
  credit,
  debit,
  companyName,
  module
}: {
  data: RowExportTypePdf[]
  column: { header: string; key: string; width: number }[]
  date?: string
  module?: string
  credit: number
  debit: number
  companyName?: string
}) {
  return (
    <Document>
      <Page orientation="landscape" size="A4" style={styles.page}>
        {module && <Text
          style={{
            fontSize: 18,
            marginTop: 30,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "heavy",
            textTransform: "uppercase"
          }}>
          {module}
        </Text>}

        <Text
          style={{
            fontSize: 18,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "heavy",
            textTransform: "uppercase"
          }}>
          {`Find Records ${module}`}
        </Text>

        <Text
          style={{
            fontSize: 12,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "medium"
          }}>
          {companyName?.toUpperCase() ?? ""}
        </Text>

        {date && <Text style={styles.date}>Date: {date}</Text>}

        <TableCell data={data} column={column} />

        <View
          style={{
            marginTop: 10,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end",
            paddingRight: 10
          }}>
          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>TOTAL DEBIT:</Text>
            <Text style={{ width: 100, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(debit).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>TOTAL CREDIT:</Text>
            <Text style={{ width: 100, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(credit).intoCurrency}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  )
}
